import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import Inicio from './pages/Inicio';
import IniciarSesion from './pages/IniciarSesion';
import Dashboard from './pages/usuario/dashboard/Dashboard';
import NuevoTicket from './pages/usuario/NuevoTicket';
import TicketCreado from './pages/usuario/TicketCreado';
import Ticket from './pages/usuario/Ticket';
import Servicios from './pages/usuario/Servicios';
import EditarPerfil from './pages/usuario/EditarPerfil';
import TicketManager from './pages/usuario/admin/TicketManager';
import UserManager from './pages/usuario/admin/UserManager';
import EmpiezaAhora from './pages/usuario/EmpiezaAhora';
import RecuperarContrasena from './pages/RecuperarContrasena';
import EditarContrasena from './pages/EditarContrasena';
import Roadmap from './pages/usuario/Roadmap';
import Error404 from './components/Error404';
import Proyectos from './pages/usuario/proyectos/TrackingProyectos';
import SingleProyecto from './pages/usuario/proyectos/SingleProyecto';
import UserProyectos from './pages/usuario/proyectos/UserProyectos';
import DetalleUsuario from './pages/usuario/admin/user/DetalleUsuario';
import AjustesDelSistema from './pages/usuario/admin/AjustesDelSistema';
import EquiposManager from './pages/usuario/admin/material/EquiposManager';
import PerifericosManager from './pages/usuario/admin/material/PerifericosManager';
import NotificacionesManager from './pages/usuario/admin/NotificacionesManager';
import ServiciosManager from './pages/usuario/admin/ServiciosManager';
import Loader from './components/Loader';

// ── Redirecciones de compatibilidad para slugs antiguos ──────────────────
// Mantienen vivos enlaces ya compartidos/guardados (bookmarks, emails
// enviados) tras el renombrado de rutas. Conservan el/los parámetro(s)
// dinámicos al redirigir.
const RedirectUsuarioLegacy = () => {
  const { usuario } = useParams();
  return <Navigate to={`/dashboard/usuarios/${usuario}`} replace />;
};

const RedirectPerifericosEquipoLegacy = () => {
  const { id } = useParams();
  return <Navigate to={`/dashboard/equipos/${id}/perifericos`} replace />;
};

const AppContent = ({ authenticated, userRole }) => {
  const canAccess = (allowedRoles) => allowedRoles.includes(userRole);

  return (
    <Suspense fallback={
      <div className="loader-fullscreen">
        <Loader />
      </div>
    }>
      <Routes>
      <Route path="/" element={<Inicio />} />
      <Route path="/registrarse" element={<EmpiezaAhora />} />
      <Route path="/recuperar-contrasena" element={<RecuperarContrasena />} />
      <Route path="/editar-contrasena" element={<EditarContrasena />} />
      <Route
        path="/iniciar-sesion"
        element={authenticated ? <Navigate to="/dashboard" /> : <IniciarSesion />}
      />
      <Route
        path="/dashboard"
        element={authenticated ? <Dashboard /> : <Navigate to="/iniciar-sesion" />}
      />
      <Route
        path="/dashboard/servicios"
        element={authenticated ? <Servicios /> : <Navigate to="/iniciar-sesion" />}
      />
      <Route
        path="/dashboard/editar-perfil"
        element={authenticated ? <EditarPerfil /> : <Navigate to="/iniciar-sesion" />}
      />
      <Route
        path="/dashboard/tickets"
        element={authenticated && canAccess(['admin', 'tecnico']) ? <TicketManager /> : <Navigate to="/dashboard" />}
      />
      <Route
        path="/dashboard/usuarios"
        element={authenticated && canAccess(['admin', 'tecnico']) ? <UserManager /> : <Navigate to={authenticated ? '/dashboard' : '/iniciar-sesion'} />}
      />
      <Route
        path="/dashboard/usuarios/:usuario"
        element={authenticated && canAccess(['admin', 'tecnico']) ? <DetalleUsuario /> : <Navigate to={authenticated ? '/dashboard' : '/iniciar-sesion'} />}
      />
      {/* Slugs antiguos: /dashboard/users(/...) → /dashboard/usuarios(/...) */}
      <Route path="/dashboard/users" element={<Navigate to="/dashboard/usuarios" replace />} />
      <Route path="/dashboard/users/:usuario" element={<RedirectUsuarioLegacy />} />

      <Route
        path="/dashboard/gestion-proyectos"
        element={authenticated && canAccess(['admin']) ? <Proyectos /> : <Navigate to="/iniciar-sesion" />}
      />
      {/* Slug antiguo: /dashboard/tracking-proyectos → /dashboard/gestion-proyectos */}
      <Route path="/dashboard/tracking-proyectos" element={<Navigate to="/dashboard/gestion-proyectos" replace />} />

      <Route
        path="/dashboard/equipos"
        element={authenticated && canAccess(['admin']) ? <EquiposManager /> : <Navigate to="/iniciar-sesion" />}
      />

      <Route
        path="/dashboard/perifericos"
        element={authenticated && canAccess(['admin']) ? <PerifericosManager /> : <Navigate to="/iniciar-sesion" />}
      />

      <Route
        path="/dashboard/equipos/:id/perifericos"
        element={authenticated && canAccess(['admin']) ? <PerifericosManager /> : <Navigate to="/iniciar-sesion" />}
      />
      {/* Slug antiguo: /dashboard/perifericos/:id (el :id era en realidad el equipo) */}
      <Route path="/dashboard/perifericos/:id" element={<RedirectPerifericosEquipoLegacy />} />

      <Route
        path="/dashboard/ajustes-del-sistema"
        element={authenticated && canAccess(['admin']) ? <AjustesDelSistema /> : <Navigate to="/iniciar-sesion" />}
      />
      <Route
        path="/dashboard/notificaciones"
        element={authenticated && canAccess(['admin']) ? <NotificacionesManager /> : <Navigate to="/iniciar-sesion" />}
      />
      <Route
        path="/dashboard/gestion-servicios"
        element={authenticated && canAccess(['admin']) ? <ServiciosManager /> : <Navigate to="/iniciar-sesion" />}
      />
      <Route
        path="/dashboard/proyectos"
        element={authenticated ? <UserProyectos /> : <Navigate to="/iniciar-sesion" />}
      />
      <Route
        path="/dashboard/proyecto/:id"
        element={authenticated ? <SingleProyecto /> : <Navigate to="/iniciar-sesion" />}
      />
      <Route
        path="/nuevo-ticket"
        element={authenticated ? <NuevoTicket /> : <Navigate to="/iniciar-sesion" />}
      />
      <Route
        path="/ticket-creado/:identificador"
        element={authenticated ? <TicketCreado /> : <Navigate to="/iniciar-sesion" />}
      />
      <Route
        path="/ticket/:identificador"
        element={authenticated ? <Ticket /> : <Navigate to="/iniciar-sesion" />}
      />
      <Route
        path="/dashboard/roadmap"
        element={authenticated ? <Roadmap /> : <Navigate to="/iniciar-sesion" />}
      />
      <Route path="*" element={<Error404 />} />
    </Routes>
    </Suspense>
  );
};

export default AppContent;
